import { PhoneNumberField } from '@armhr/ui';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

interface AddContactDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (contact: {
    name: string;
    email: string;
    phone: string;
    title: string;
    department: string;
  }) => void;
  employees: PublicEmployeeProfile[];
  departmentCodes?: { id: string; value: string }[];
}

const AddContactDialog: React.FC<AddContactDialogProps> = ({
  open,
  onClose,
  onAdd,
  employees,
  departmentCodes,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [selectedEmployee, setSelectedEmployee] =
    useState<PublicEmployeeProfile | null>(null);

  const resolveDepartmentLabel = (code: string | undefined): string => {
    if (!code || !departmentCodes) return '';
    const match = departmentCodes.find((d) => d.id === code);
    return match?.value || code;
  };

  const handleEmployeeSelect = (
    _: React.SyntheticEvent,
    employee: PublicEmployeeProfile | null
  ) => {
    setSelectedEmployee(employee);
    if (employee) {
      const displayName = employee.nickname
        ? `${employee.nickname} ${employee.last_name}`
        : `${employee.first_name} ${employee.last_name}`;
      setName(displayName);
      setEmail(employee.work_email_address || employee.email_address || '');
      setPhone(employee.mobile || '');
      setTitle(employee.position?.employee_title || '');
      setDepartment(resolveDepartmentLabel(employee.position?.department));
    }
  };

  const handleAdd = () => {
    if (!name.trim()) return;
    onAdd({
      name: name.trim(),
      email,
      phone,
      title,
      department: department.trim(),
    });
    handleReset();
  };

  const handleReset = () => {
    setName('');
    setEmail('');
    setPhone('');
    setTitle('');
    setDepartment('');
    setSelectedEmployee(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Contact</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Add an employee, distribution list, or any external contact. Only a
          name is required — all other fields are optional.
        </Typography>

        <Box
          sx={{
            bgcolor: 'action.hover',
            borderRadius: 1.5,
            px: 2,
            py: 1.5,
            mb: 1,
          }}
        >
          <Autocomplete
            options={employees}
            getOptionLabel={(option) => {
              const display = option.nickname || option.first_name;
              return `${display} ${option.last_name}`;
            }}
            value={selectedEmployee}
            onChange={handleEmployeeSelect}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search employees (optional)"
                size="small"
                helperText="Pre-fills the fields below — or skip and enter below"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Typography fontSize={14}>
                  <span>{option.nickname || option.first_name}</span>{' '}
                  <span>{option.last_name}</span>
                </Typography>
                {option.position?.employee_title && (
                  <Typography
                    fontSize={12}
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    — {option.position.employee_title}
                  </Typography>
                )}
              </Box>
            )}
            noOptionsText="No employees found"
          />
        </Box>

        <Divider sx={{ my: 2.5 }}>
          <Typography variant="caption" color="text.secondary">
            Contact details
          </Typography>
        </Divider>

        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          required
          sx={{ mb: 2 }}
        />
        <TextField
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          size="small"
          type="email"
          placeholder="e.g., hr-team@company.com"
          helperText="Optional"
          sx={{ mb: 2 }}
        />
        <PhoneNumberField
          label="Phone"
          name="phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
          size="small"
          helperText="Optional"
          sx={{ mb: 2 }}
        />
        <TextField
          label="Title / Role"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          size="small"
          helperText="Optional"
          sx={{ mb: 2 }}
        />
        <TextField
          label="Department"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          fullWidth
          size="small"
          placeholder="e.g., Payroll, HR, Benefits"
          helperText="Optional"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleAdd} variant="contained" disabled={!name.trim()}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddContactDialog;
